'use client'

/* eslint-disable @next/next/no-img-element */

import type { ReactNode } from 'react'
import { useState } from 'react'
import { landingpage_skills_top_copy_click } from '@/constants/trace'
import { cn, trackEvent } from '@/lib/utils'
import { Check } from 'lucide-react'
import { allSkillPrompt, HERO_VISUAL_IMAGE } from './skill-data'
import { copySkillText } from './skill-copy'

// Hero 自己管理复制和滚动交互，page.tsx 只负责装配，这样 SSR 边界就不会被整页状态拖垮。
export function SkillHeroSection() {
  const [copied, setCopied] = useState(false)

  const handleCopyAll = async () => {
    trackEvent(landingpage_skills_top_copy_click)
    const success = await copySkillText(allSkillPrompt)

    if (!success) {
      setCopied(false)
      return
    }

    setCopied(true)
    window.setTimeout(() => {
      setCopied(false)
    }, 1600)
  }

  const handleBrowseAll = () => {
    document.getElementById('skill-marquee-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <section className="relative overflow-hidden border border-white/70 bg-[linear-gradient(180deg,#EAECFE_0%,#F4F7FD_100%)] pt-56">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="mx-auto flex h-full w-full max-w-[1400px] items-end justify-end px-6 2xl:px-0">
          {/* 小一档桌面宽度先收住主视觉，避免图片压到标题；超大屏再恢复到设计稿比例。 */}
          <div className="w-[800px] translate-x-6 opacity-40 xl:opacity-70 2xl:translate-x-10 2xl:opacity-100">
            <img src={HERO_VISUAL_IMAGE} alt="" aria-hidden="true" className="h-full w-full object-contain object-right" />
          </div>
        </div>
      </div>

      <div className="relative mx-auto flex min-h-[560px] w-full max-w-[1200px] items-start px-4 md:min-h-[620px] md:px-8 xl:min-h-[700px]">
        <div className="relative z-10 flex w-full flex-col items-center xl:max-w-[670px] xl:items-start">
          <div className="space-y-4 md:space-y-5 xl:space-y-6">
            <h1 className="text-center text-[40px] font-extrabold leading-[1.18] tracking-[-0.03em] text-[#353745] md:text-[56px] xl:text-left xl:text-[64px]">
              把<span className="text-[#685EF6]">爱图表的能力</span>
              <br />
              带到更多使用场景里
            </h1>
            <p className="w-full max-w-[720px] text-center text-[18px] leading-[1.7] text-[#555974] md:text-[22px] md:leading-[1.6] xl:text-left xl:text-[24px] xl:leading-[42px]">
              围绕汇报、复盘、展示和传播场景，提供可直接安装的 Skill，让图表、大屏和视觉化内容更快进入业务流程。
            </p>
          </div>

          <div className="mt-12 flex flex-col items-center gap-4 sm:mt-16 sm:gap-5 xl:mt-24 xl:flex-row xl:items-center xl:gap-6">
            <SkillHeroActionButton
              onClick={handleCopyAll}
              copied={copied}
              primary
              icon={<CopyIcon />}
            >
              复制全部技能安装提示词
            </SkillHeroActionButton>
            <SkillHeroActionButton onClick={handleBrowseAll} copied={false}>
              浏览全部Skill
            </SkillHeroActionButton>
          </div>

          <p className="mt-4 text-center text-[14px] leading-[1.6] text-[#898FA7] md:mt-5 md:text-[16px] xl:text-left xl:text-[20px] xl:leading-[20px]">
            复制提示词 → 发给你的小龙虾 → 自动学会
          </p>
        </div>
      </div>

    </section>
  )
}

type SkillHeroActionButtonProps = {
  children: ReactNode
  onClick: () => void
  copied: boolean
  primary?: boolean
  icon?: ReactNode
}

// Hero 和底部 CTA 共用同一套按钮壳，统一视觉，同时把交互各自留在所属 section 内。
export function SkillHeroActionButton({ children, onClick, copied, primary = false, icon }: SkillHeroActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex min-h-[56px] w-full max-w-full items-center justify-center gap-2.5 rounded-[35.914px] px-5 text-center text-[16px] leading-none transition duration-200 hover:-translate-y-0.5 sm:min-h-[60px] sm:px-6 sm:text-[18px] xl:h-[68px] xl:px-[30px] xl:text-[24px]',
        primary
          ? 'border border-[rgba(51,51,51,0.46)] bg-[linear-gradient(180deg,#685EF6_0%,#8164FF_100%)] text-white shadow-[0px_7px_6.1px_rgba(33,21,208,0.08)] sm:w-[380px]'
          : 'border border-[#E1E1E1] bg-white text-[#353745] shadow-[0px_6px_12.5px_rgba(0,0,0,0.03),inset_0px_4px_10px_rgba(255,255,255,1)] sm:w-[225px]'
      )}
    >
      {icon ? copied ? <CopySuccessIcon /> : icon : null}
      <span>{copied ? '已复制提示词' : children}</span>
    </button>
  )
}

export function CopyIcon() {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true" className="h-[30px] w-[30px] shrink-0">
      <path
        d="M7.5 8.99375V5.81812C7.5 4.75022 8.287 3.88452 9.25781 3.88452H24.4922C25.463 3.88452 26.25 4.75022 26.25 5.81812V22.5759C26.25 23.6438 25.463 24.5095 24.4922 24.5095H21.5727"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19.043 9.50952H5.33203C4.4583 9.50952 3.75 10.2965 3.75 11.2673V26.5017C3.75 27.4725 4.4583 28.2595 5.33203 28.2595H19.043C19.9167 28.2595 20.625 27.4725 20.625 26.5017V11.2673C20.625 10.2965 19.9167 9.50952 19.043 9.50952Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function CopySuccessIcon() {
  return <Check aria-hidden="true" className="h-[30px] w-[30px] shrink-0" strokeWidth={2.4} />
}
